import { useState, useEffect } from "react";
import { ArrowRight, ArrowLeft, Check, Package, Factory, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";

const STEPS = [
  { id: 1, title: "EAN", icon: Package },
  { id: 2, title: "Artikelnummer", icon: Package },
  { id: 3, title: "Tillverkare", icon: Factory },
  { id: 4, title: "Kategori", icon: Tag },
];

// Validate EAN format (8 or 13 digits)
const isValidEan = (ean) => {
  if (!ean) return false;
  const cleanEan = ean.replace(/\s/g, '');
  return /^\d{8}$|^\d{13}$/.test(cleanEan);
};

export function NewProductDialog({
  open,
  onOpenChange,
  newProduct,
  onProductChange,
  manufacturers,
  categories,
  onSave,
  processing,
}) {
  const [currentStep, setCurrentStep] = useState(1);
  const [manufacturerOpen, setManufacturerOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);

  // Reset step when dialog opens
  useEffect(() => {
    if (open) {
      setCurrentStep(1);
      setManufacturerOpen(false);
      setCategoryOpen(false);
    }
  }, [open]);

  const handleClose = () => {
    setCurrentStep(1);
    onOpenChange(false);
  };

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    } else {
      onSave();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const canProceed = () => {
    if (currentStep === 1) return isValidEan(newProduct.ean);
    if (currentStep === 2) return newProduct.artikelnummer?.trim().length > 0;
    if (currentStep === 3) return newProduct.tillverkare?.trim().length > 0;
    if (currentStep === 4) return newProduct.kategori?.trim().length > 0;
    return true;
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && canProceed()) {
      handleNext();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md" data-testid="new-product-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-primary" />
            Ny produkt
          </DialogTitle>
          <DialogDescription className="flex items-center gap-2">
            <span className="font-mono text-foreground">{newProduct.ean}</span>
          </DialogDescription>
        </DialogHeader>

        {/* Step indicators */}
        <div className="flex items-center justify-between mb-6">
          {STEPS.map((step, index) => {
            const StepIcon = step.icon;
            const isActive = currentStep === step.id;
            const isCompleted = currentStep > step.id;
            
            return (
              <div key={step.id} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div
                    className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center transition-colors",
                      isActive && "bg-primary text-primary-foreground",
                      isCompleted && "bg-green-500 text-white",
                      !isActive && !isCompleted && "bg-muted text-muted-foreground"
                    )}
                  >
                    {isCompleted ? (
                      <Check className="h-5 w-5" />
                    ) : (
                      <StepIcon className="h-5 w-5" />
                    )}
                  </div>
                  <span className={cn(
                    "text-xs mt-1 font-medium",
                    isActive && "text-primary",
                    isCompleted && "text-green-500",
                    !isActive && !isCompleted && "text-muted-foreground"
                  )}>
                    {step.title}
                  </span>
                </div>
                {index < STEPS.length - 1 && (
                  <div className={cn(
                    "w-12 h-0.5 mx-2 mt-[-20px]",
                    isCompleted ? "bg-green-500" : "bg-muted"
                  )} />
                )}
              </div>
            );
          })}
        </div>

        {/* Step content */}
        <div className="min-h-[140px]">
          {currentStep === 1 && (
            <div className="space-y-4" data-testid="step-ean">
              <div>
                <Label className="text-base font-medium">EAN-kod *</Label>
                <p className="text-sm text-muted-foreground mb-3">
                  Ange produktens EAN-kod (8 eller 13 siffror)
                </p>
                <Input
                  value={newProduct.ean}
                  onChange={(e) => onProductChange({ ...newProduct, ean: e.target.value.replace(/\D/g, '') })}
                  onKeyDown={handleKeyDown}
                  placeholder="T.ex. 7350123456789"
                  className="text-lg h-12 font-mono"
                  autoFocus
                  maxLength={13}
                  data-testid="new-product-ean"
                />
                {newProduct.ean && !isValidEan(newProduct.ean) && (
                  <p className="text-sm text-destructive mt-2">
                    EAN måste vara 8 eller 13 siffror
                  </p>
                )}
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-4" data-testid="step-artikelnummer">
              <div>
                <Label className="text-base font-medium">Artikelnummer *</Label>
                <p className="text-sm text-muted-foreground mb-3">
                  Ange produktens artikelnummer
                </p>
                <Input
                  value={newProduct.artikelnummer}
                  onChange={(e) => onProductChange({ ...newProduct, artikelnummer: e.target.value.toUpperCase() })}
                  onKeyDown={handleKeyDown}
                  placeholder="T.ex. ABC-123"
                  className="uppercase text-lg h-12"
                  autoFocus
                  data-testid="new-product-artikelnummer"
                />
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="space-y-4" data-testid="step-tillverkare">
              <div>
                <Label className="text-base font-medium">Tillverkare *</Label>
                <p className="text-sm text-muted-foreground mb-3">
                  Välj tillverkare
                </p>
                <Popover open={manufacturerOpen} onOpenChange={setManufacturerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      className="w-full justify-between h-12 text-base"
                      data-testid="manufacturer-selector"
                    >
                      {newProduct.tillverkare || "Välj tillverkare..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Sök tillverkare..." />
                      <CommandList className="max-h-[200px]">
                        <CommandEmpty>Ingen hittad</CommandEmpty>
                        <CommandGroup>
                          {manufacturers.map((m) => (
                            <CommandItem
                              key={m}
                              value={m}
                              onSelect={() => {
                                onProductChange({ ...newProduct, tillverkare: m });
                                setManufacturerOpen(false);
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  newProduct.tillverkare === m ? "opacity-100" : "opacity-0"
                                )}
                              />
                              {m}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {newProduct.tillverkare && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-2 text-muted-foreground"
                    onClick={() => onProductChange({ ...newProduct, tillverkare: "" })}
                  >
                    Rensa val
                  </Button>
                )}
              </div>
            </div>
          )}

          {currentStep === 4 && (
            <div className="space-y-4" data-testid="step-kategori">
              <div>
                <Label className="text-base font-medium">Kategori *</Label>
                <p className="text-sm text-muted-foreground mb-3">
                  Välj kategori
                </p>
                <Popover open={categoryOpen} onOpenChange={setCategoryOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      className="w-full justify-between h-12 text-base"
                      data-testid="category-selector"
                    >
                      {newProduct.kategori || "Välj kategori..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Sök kategori..." />
                      <CommandList className="max-h-[200px]">
                        <CommandEmpty>Ingen hittad</CommandEmpty>
                        <CommandGroup>
                          {categories.map((c) => (
                            <CommandItem
                              key={c}
                              value={c}
                              onSelect={() => {
                                onProductChange({ ...newProduct, kategori: c });
                                setCategoryOpen(false);
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  newProduct.kategori === c ? "opacity-100" : "opacity-0"
                                )}
                              />
                              {c}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {newProduct.kategori && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-2 text-muted-foreground"
                    onClick={() => onProductChange({ ...newProduct, kategori: "" })}
                  >
                    Rensa val
                  </Button>
                )}
              </div>

              {/* Summary before save */}
              <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                <p className="text-sm font-medium mb-2">Sammanfattning:</p>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline" className="font-mono">{newProduct.ean}</Badge>
                  <Badge>{newProduct.artikelnummer}</Badge>
                  {newProduct.tillverkare && <Badge variant="secondary">{newProduct.tillverkare}</Badge>}
                  {newProduct.kategori && <Badge variant="secondary">{newProduct.kategori}</Badge>}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation buttons */}
        <div className="flex justify-between pt-4 border-t">
          <Button
            variant="outline"
            onClick={currentStep === 1 ? handleClose : handleBack}
            data-testid="wizard-back-btn"
          >
            {currentStep === 1 ? (
              "Avbryt"
            ) : (
              <>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Tillbaka
              </>
            )}
          </Button>
          
          <Button
            onClick={handleNext}
            disabled={!canProceed() || processing}
            variant={canProceed() ? "default" : "outline"}
            data-testid="wizard-next-btn"
          >
            {processing ? (
              "Sparar..."
            ) : currentStep === 4 ? (
              <>
                <Check className="h-4 w-4 mr-2" />
                Skapa produkt
              </>
            ) : (
              <>
                Nästa
                <ArrowRight className="h-4 w-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
